import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import toast from 'react-hot-toast';

import { getQboSession, getQboLastLogin, getThirdPartyStepsCompleted, getThirdPartyImportedStep } from '../../../store/settings/selectors';
import { getBranch } from '../../../store/user/selectors';
import { URLS } from '../../../resources/constants';
import { useThirdPartyApi } from '../../../resources/hooks/api/thirdPartyApiHook';
import { useSettingsApi } from '../../../resources/hooks/api/useSettingsApiHook';

import BeginningBalanceImportModal from '../../../components/Modal/BeginningBalanceImport/BeginningBalanceImportModal';
import QBValidationReportModal from '../../../components/Modal/QBValidationReport/QBValidationReportModal';
import QboBackupStatus from './QboBackupStatus';
import BalanceSheetRefreshStatus from './BalanceSheetRefreshStatus';
import './Dashboard.css';

interface WizardStep {
    value: number;
    title: string;
    description: string;
    icon: string;
}

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation("scenes.thirdparty.quickbook.Dashboard");

    const quickBookSession = useSelector(getQboSession);
    const quickBookLastLogin = useSelector(getQboLastLogin);
    const quickBookStepCompleted = useSelector(getThirdPartyStepsCompleted);
    const quickBookImportedStep = useSelector(getThirdPartyImportedStep);
    const branchData = useSelector(getBranch);

    const { fetchAccountSettingsData } = useSettingsApi();
    const { quickBookImportData } = useThirdPartyApi();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isOpenBBModal, setIsOpenBBModal] = useState<boolean>(false);
    const [isOpenValidationModal, setIsOpenValidationModal] = useState<boolean>(false);
    const [selectedDateBB, setSelectedDateBB] = useState<Date[]>([]);
    const [showBackupDetails, setShowBackupDetails] = useState<boolean>(true);
    const [backupStatus, setBackupStatus] = useState<string>('ready');

    const getBackupBadge = (status: string) => {
        switch (status) {
            case 'completed':
                return { label: t('status.completed'), bgClass: 'bg-success text-white', icon: 'fa-check-circle' };
            case 'in_progress':
                return { label: t('status.running'), bgClass: 'bg-info text-white', icon: 'fa-spinner fa-spin' };
            case 'failed':
                return { label: t('status.failed'), bgClass: 'bg-danger text-white', icon: 'fa-exclamation-triangle' };
            default:
                return { label: t('status.ready'), bgClass: 'bg-secondary text-white', icon: 'fa-shield-alt' };
        }
    };

    const wizardSteps: WizardStep[] = [
        {
            value: 1,
            title: t("wizard.steps.importCOA"),
            description: t("wizard.descriptions.importCOA"),
            icon: "fa-sitemap",
        },
        {
            value: 2,
            title: t("wizard.steps.importBB"),
            description: t("wizard.descriptions.importBB"),
            icon: "fa-scale-balanced",
        },
        {
            value: 3,
            title: t("wizard.steps.importBSPL"),
            description: t("wizard.descriptions.importBSPL"),
            icon: "fa-file-invoice-dollar",
        },
        {
            value: 4,
            title: t("wizard.steps.importVendor"),
            description: t("wizard.descriptions.importVendor"),
            icon: "fa-store",
        },
        {
            value: 5,
            title: t("wizard.steps.importCustomer"),
            description: t("wizard.descriptions.importCustomer"),
            icon: "fa-users",
        },
    ];

    useEffect(() => {
        if (quickBookSession) {
            navigate(URLS.THIRD_PARTY_CONNECTION);
        }
        if (!branchData?.id) return;
        fetchAccountSettingsData({ branch_id: +branchData['id'] }, () => {}, () => {});
    }, [branchData?.id]);

    const _handleQuickBookImportData = (step: number, datesOverride?: Date[]) => {
        if (!branchData?.id) return;

        const importedStep = +quickBookImportedStep || 0;
        const dates = datesOverride && datesOverride.length > 0 ? datesOverride : selectedDateBB;

        if (step === 1 && importedStep >= 1) {
            navigate(URLS.QUICKBOOK_COA);
            return;
        }
        if (step === 2) {
            navigate(URLS.QUICKBOOK_BB);
            return;
        }
        if (step === 3) {
            if (importedStep >= 3 || +quickBookStepCompleted >= 3) {
                navigate(URLS.QUICKBOOK_BS_PL);
                return;
            }
            if (!dates || !dates[0] || !dates[1]) {
                setIsOpenBBModal(true);
                return;
            }
        }

        const params: any = {
            branch_id: +branchData['id'],
            step,
        };

        if (step === 2) {
            params['bs_import_start_date'] = moment(dates[0]).startOf('month').format('YYYY-MM-DD');
            params['bs_import_end_date'] = moment(dates[0]).endOf('month').format('YYYY-MM-DD');
        } else if (step === 3) {
            params['bs_import_start_date'] = moment(dates[0]).startOf('month').format('YYYY-MM-DD');
            params['bs_import_end_date'] = moment(dates[1]).format('YYYY-MM-DD');
        } else if (step > 5) {
            navigate(URLS.DASHBOARD);
            return;
        }

        setIsLoading(true);
        quickBookImportData(
            params,
            (message: string, resp: any) => {
                setIsLoading(false);
                setSelectedDateBB([]);
                toast.success(message);
                if (step === 1) {
                    navigate(URLS.QUICKBOOK_COA);
                } else if (step === 2) {
                    navigate(URLS.QUICKBOOK_BB);
                } else if (step === 3) {
                    localStorage.setItem("qbo_duration", moment(params['bs_import_end_date']).diff(moment(params['bs_import_start_date']), 'months') + 1 + "");
                    if (resp?.data?.data?.job_id) {
                        localStorage.setItem("qbo_import_job_id", resp.data.data.job_id);
                    }
                    navigate(URLS.QUICKBOOK_BS_PL);
                } else {
                    fetchAccountSettingsData({ branch_id: +branchData['id'] }, () => {}, () => {});
                }
            },
            (message: string) => {
                setIsLoading(false);
                setSelectedDateBB([]);
                toast.error(message);
            }
        );
    };

    const _handleBBDateSelection = (dates?: Date[]) => {
        _handleQuickBookImportData(+quickBookStepCompleted + 1, dates);
    };

    const completedStepsCount = Math.min(5, Math.max(0, +quickBookStepCompleted));
    const progressPercent = Math.round((completedStepsCount / 5) * 100);
    const isAllStepsCompleted = +quickBookStepCompleted >= 5;
    const canRunValidation = +quickBookStepCompleted >= 3 || +quickBookImportedStep >= 3;
    const nextStepNumber = Math.min(5, +quickBookStepCompleted + 1);

    const handleFooterButtonClick = () => {
        if (isAllStepsCompleted) {
            navigate(URLS.DASHBOARD);
        } else {
            _handleQuickBookImportData(nextStepNumber);
        }
    };

    return (
        <div className="main-container flex-grow-1 bg-light">
            <div className="container-fluid py-4 qbo-wizard-container">
                {/* --- HERO HEADER BANNER --- */}
                <div className="qbo-wizard-hero mb-4">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
                        <div className="qbo-hero-brand">
                            <div className="qbo-logo-badge">
                                <img src="quick-book.svg" alt="QuickBooks Online" />
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-3 mb-1">
                                    <h2 className="h4 font-weight-700 text-white mb-0">{t("header.title")}</h2>
                                    <span className="qbo-connection-status">
                                        <span className="qbo-pulse-dot"></span>
                                        {t("connection.connected")}
                                    </span>
                                </div>
                                <p className="text-white-50 small mb-0">
                                    {t("connection.connectedOn")}{quickBookLastLogin ? moment(quickBookLastLogin).locale(i18n.language).format('MMMM Do, YYYY') : t("connection.active")}
                                </p>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-light btn-sm px-3 d-inline-flex align-items-center gap-2"
                                onClick={() => setShowBackupDetails(prev => !prev)}
                            >
                                <i className="fas fa-database"></i>
                                <span>{showBackupDetails ? t('backup.hideDetails') : t('backup.showStatus')}</span>
                                <span className={`badge ${getBackupBadge(backupStatus).bgClass} ms-1`} style={{ fontSize: '11px', fontWeight: 600 }}>
                                    <i className={`fas ${getBackupBadge(backupStatus).icon} me-1`}></i>
                                    {getBackupBadge(backupStatus).label}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="btn btn-light btn-sm px-3 font-weight-600 text-primary"
                                onClick={() => navigate(URLS.THIRD_PARTY_CONNECTION)}
                            >
                                <i className="fas fa-sync-alt me-1"></i> {t("actions.reconnect")}
                            </button>
                        </div>
                    </div>
                </div>

                {/* --- BALANCE SHEET REFRESH PROGRESS (READ-ONLY POLL, ONLY SHOWS WHEN A REFRESH IS RUNNING) --- */}
                <BalanceSheetRefreshStatus branchId={+branchData['id']} />

                {/* --- FREEZERDOC STORAGE BACKUP PIPELINE STATUS (SHOWN BY DEFAULT, TOGGLEABLE) --- */}
                <div style={{ display: showBackupDetails ? 'block' : 'none' }}>
                    <div className="card shadow-sm border-0 mb-4 rounded-3">
                        <div className="card-body p-3">
                            <QboBackupStatus branchId={+branchData['id']} onStatusChange={setBackupStatus} />
                        </div>
                    </div>
                </div>

                {/* --- OVERALL PROGRESS CARD --- */}
                <div className="qbo-progress-card mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="d-flex align-items-center gap-2">
                            <i className="fas fa-tasks text-primary"></i>
                            <h6 className="font-weight-700 mb-0">{t("progress.title")}</h6>
                        </div>
                        <span className="badge bg-primary-subtle text-primary font-weight-700 px-3 py-1 rounded-pill" style={{ fontSize: '13px' }}>
                            {t("progress.summary", { percent: progressPercent, completed: completedStepsCount, total: 5 })}
                        </span>
                    </div>

                    <div className="qbo-progress-bar-bg mb-4">
                        <div className="qbo-progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
                    </div>

                    {/* Stepper Navigation Indicator */}
                    <div className="qbo-wizard-stepper-bar">
                        {wizardSteps.map((step) => {
                            const isCompleted = step.value <= +quickBookStepCompleted;
                            const isActive = step.value === nextStepNumber;

                            return (
                                <div
                                    key={step.value}
                                    className={`qbo-stepper-item ${isCompleted ? 'completed' : isActive ? 'active' : ''}`}
                                    onClick={() => {
                                        if (isCompleted || isActive) {
                                            _handleQuickBookImportData(step.value);
                                        }
                                    }}
                                >
                                    <div className="qbo-stepper-circle">
                                        {isCompleted ? <i className="fas fa-check"></i> : step.value}
                                    </div>
                                    <span className="qbo-stepper-label">{step.title}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* --- STEP CARDS LIST --- */}
                <div className="qbo-step-cards-container mb-4">
                    {wizardSteps.map((step) => {
                        const isCompleted = step.value <= +quickBookStepCompleted;
                        const isImported = step.value <= +quickBookImportedStep;
                        const isNextTarget = step.value === nextStepNumber;

                        let cardStateClass = 'upcoming';
                        if (isCompleted) cardStateClass = 'completed';
                        else if (isNextTarget) cardStateClass = 'active';

                        return (
                            <div key={step.value} className={`qbo-step-card ${cardStateClass}`}>
                                <div className="d-flex align-items-center gap-3">
                                    <div className="qbo-step-icon-box">
                                        <i className={`fas ${step.icon}`}></i>
                                    </div>

                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <span className="text-muted small font-weight-700 uppercase" style={{ letterSpacing: '0.5px' }}>
                                                {t("step.label", { number: String(step.value).padStart(2, '0') })}
                                            </span>
                                            {isCompleted && (
                                                <span className="qbo-step-badge-done">
                                                    <i className="fas fa-check-circle me-1"></i> {t("step.badge.completed")}
                                                </span>
                                            )}
                                            {isNextTarget && !isCompleted && (
                                                <span className="qbo-step-badge-next">
                                                    <i className="fas fa-play me-1"></i> {t("step.badge.readyToImport")}
                                                </span>
                                            )}
                                            {!isCompleted && !isNextTarget && (
                                                <span className="qbo-step-badge-pending">
                                                    <i className="fas fa-lock me-1"></i> {t("step.badge.pendingPrevious")}
                                                </span>
                                            )}
                                        </div>

                                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <h5 className="font-weight-700 mb-0" style={{ color: '#1e293b' }}>
                                                {step.title}
                                            </h5>
                                            {step.value === 2 && quickBookStepCompleted >= 2 && (() => {
                                                const storedDate = localStorage.getItem('bbSelectedDate');
                                                if (storedDate) {
                                                    try {
                                                        const parsed = JSON.parse(storedDate);
                                                        const dStr = Array.isArray(parsed) ? parsed[0] : parsed;
                                                        if (dStr && moment(dStr).isValid()) {
                                                            return (
                                                                <span className="badge bg-light text-primary border border-primary-subtle font-weight-600 px-2 py-1 ms-2" style={{ fontSize: '0.8rem', borderRadius: '6px' }}>
                                                                    <i className="far fa-calendar-alt text-primary me-1"></i> {t("step.asOf", { date: moment(dStr).locale(i18n.language).format('MMMM YYYY') })}
                                                                </span>
                                                            );
                                                        }
                                                    } catch (e) {}
                                                }
                                                return null;
                                            })()}
                                        </div>
                                        <p className="text-muted small mb-0" style={{ maxWidth: '750px', lineHeight: '1.5' }}>
                                            {step.description}
                                        </p>
                                    </div>
                                </div>

                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                    {isCompleted ? (
                                        (() => {
                                            const getStepCompletedButtonConfig = (stepValue: number) => {
                                                switch (stepValue) {
                                                    case 1:
                                                        return { label: t('step.view.coa'), icon: 'fa-sitemap' };
                                                    case 2:
                                                        return { label: t('step.view.openingBalances'), icon: 'fa-scale-balanced' };
                                                    case 3:
                                                        return { label: t('step.view.bspl'), icon: 'fa-file-invoice-dollar' };
                                                    case 4:
                                                        return { label: t('step.view.vendor'), icon: 'fa-store' };
                                                    case 5:
                                                        return { label: t('step.view.customer'), icon: 'fa-users' };
                                                    default:
                                                        return { label: t('step.view.default'), icon: 'fa-folder-open' };
                                                }
                                            };
                                            const config = getStepCompletedButtonConfig(step.value);
                                            return (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-success btn-sm px-3 rounded-pill font-weight-600 d-inline-flex align-items-center gap-1"
                                                    onClick={() => _handleQuickBookImportData(step.value)}
                                                >
                                                    <i className={`fas ${config.icon}`}></i>
                                                    <span>{config.label}</span>
                                                </button>
                                            );
                                        })()
                                    ) : isNextTarget ? (
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm px-4 rounded-pill font-weight-600 shadow-sm"
                                            onClick={() => _handleQuickBookImportData(step.value)}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                            ) : (
                                                <>
                                                    <span>{t("step.startImport", { step: step.value })}</span>
                                                    <i className="fas fa-arrow-right ms-2"></i>
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-light btn-sm px-3 rounded-pill text-muted"
                                            disabled
                                        >
                                            <i className="fas fa-lock me-1"></i> {t("step.locked")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* --- FOOTER ACTION BAR --- */}
                <div className="qbo-wizard-footer-bar">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <span className="text-muted small">{isAllStepsCompleted ? t('footer.status') : t('footer.currentTarget')}</span>
                            {backupStatus === 'failed' && (
                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill font-weight-600 px-2.5 py-0.5" style={{ fontSize: '11px' }}>
                                    <i className="fas fa-exclamation-circle me-1"></i> {t("footer.backupFailed")}
                                </span>
                            )}
                            {backupStatus === 'in_progress' && (
                                <span className="badge bg-info-subtle text-info border border-info-subtle rounded-pill font-weight-600 px-2.5 py-0.5" style={{ fontSize: '11px' }}>
                                    <i className="fas fa-spinner fa-spin me-1"></i> {t("footer.backupSyncing")}
                                </span>
                            )}
                            {backupStatus === 'completed' && (
                                <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill font-weight-600 px-2.5 py-0.5" style={{ fontSize: '11px' }}>
                                    <i className="fas fa-archive me-1 text-success"></i> {t("footer.backupSecured")}
                                </span>
                            )}
                        </div>
                        <strong className="d-block text-dark">
                            {isAllStepsCompleted
                                ? t('footer.allComplete', { total: 5 })
                                : t('footer.stepOf', { step: nextStepNumber, total: 5, title: wizardSteps[nextStepNumber - 1]?.title })}
                        </strong>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        {/* Steps 4-5 import vendor/customer master records, which no validation check
                            reads. Gating on step 3 makes the report available as soon as the COA,
                            opening balances and BS/PL it actually inspects are in. */}
                        <button
                            type="button"
                            className={`btn px-4 rounded-pill font-weight-600 d-inline-flex align-items-center gap-2 ${canRunValidation ? 'btn-outline-danger shadow-sm' : 'btn-outline-secondary'}`}
                            onClick={() => setIsOpenValidationModal(true)}
                            disabled={!canRunValidation}
                            title={canRunValidation
                                ? t('validation.tooltipEnabled')
                                : t('validation.tooltipDisabled')}
                            style={canRunValidation ? undefined : { cursor: 'not-allowed', pointerEvents: 'auto' }}
                        >
                            <span className={`material-symbols-outlined ${canRunValidation ? 'text-danger' : 'text-muted'}`} style={{ fontSize: '18px' }}>task</span>
                            <span>{t('wizard.steps.validationReport')}</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary px-4 rounded-pill font-weight-600 shadow-sm"
                            onClick={handleFooterButtonClick}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            ) : (
                                <>
                                    <span>{isAllStepsCompleted ? t('footer.goToDashboard') : t('footer.continueToStep', { step: nextStepNumber })}</span>
                                    <i className="fas fa-arrow-right ms-2"></i>
                                </>
                            )}
                        </button>
                    </div>
                </div>
                {isOpenBBModal && (
                    <BeginningBalanceImportModal
                        step={nextStepNumber}
                        closeModal={() => setIsOpenBBModal(false)}
                        selectDate={setSelectedDateBB}
                        handleBBDateSelection={_handleBBDateSelection}
                    />
                )}
                {isOpenValidationModal && (
                    <QBValidationReportModal
                        branchId={+branchData['id']}
                        closeModal={() => setIsOpenValidationModal(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default Dashboard;
